import { Component } from 'react';

/**
 * Global ErrorBoundary — Flythouse
 * ─────────────────────────────────────────────────────────────────
 * Catches render / lifecycle crashes anywhere in the tree and:
 *   1. Detects the "stale chunk after redeploy" class of bugs
 *      ("Cannot access X before initialization" / "ChunkLoadError" /
 *      "Loading chunk N failed") and self-heals by clearing browser
 *      caches + service worker + hard-reloading, ONCE per session.
 *   2. Falls back to a warm, on-brand recovery screen only after the
 *      auto-heal attempt has already run — so most users never see
 *      an error at all after a deploy.
 *   3. Keeps technical details tucked behind a collapsed <details>
 *      so the screen doesn't feel like a red stack-trace nightmare.
 *
 * Async errors are caught via window.addEventListener('error') and
 * 'unhandledrejection' below so they route into the same fallback.
 */

const RECOVERY_FLAG = 'dl_error_recovery_v1';
const RECOVERY_TTL_MS = 60 * 1000; // give one healed reload 60s to prove itself

const STALE_CHUNK_PATTERNS = [
  /before initialization/i,           // "Cannot access 'X' before initialization" — hash mismatch
  /chunk[\s-]?load(ing)?[\s-]?error/i, // Webpack ChunkLoadError
  /loading chunk .+ failed/i,          // Legacy webpack message
  /failed to fetch dynamically imported module/i, // Vite / esm
  /error loading dynamically imported module/i,
  /unexpected token '<'/i,             // Chunk 404 → served index.html HTML instead of JS
  /importscripts.*failed/i,            // Service worker chunk load
];

function isStaleChunkError(error) {
  const msg = String(error?.message || error || '');
  return STALE_CHUNK_PATTERNS.some((p) => p.test(msg));
}

async function nukeAllCaches() {
  // Wipe every browser-side cache we can reach so the next reload
  // pulls fresh JS/CSS/HTML from the origin. Best-effort; each step
  // is independently guarded so a single failure doesn't block the
  // rest.
  try {
    if (typeof caches !== 'undefined' && caches?.keys) {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n).catch(() => {})));
    }
  } catch { /* ignore */ }
  try {
    if (navigator?.serviceWorker?.getRegistrations) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
    }
  } catch { /* ignore */ }
  try {
    // Clear the stored crash so a healed reload doesn't retry the
    // same message forever.
    localStorage.removeItem('dl_last_crash_v1');
    // Bump a version key so any code checking for it forces a new
    // fetch of user-facing state on remount.
    sessionStorage.setItem('dl_healed_at', String(Date.now()));
  } catch { /* ignore */ }
}

function hardReload() {
  // iter364 · On native iOS Capacitor, redirecting to the backend
  // /api/heal URL takes users OUT of the local bundle and into a
  // Safari-style URL bar — the "loading screen" that trips people
  // up during the claim flow. Stay in-app on native: nuke caches
  // (already done by callers) then do a same-origin reload which
  // Capacitor handles cleanly.
  const isNative = typeof window !== 'undefined' && window?.Capacitor?.isNativePlatform?.();
  if (isNative) {
    try { window.location.reload(); return; } catch { /* fall through */ }
  }
  // iter220 · Route the reload through the backend's Clear-Site-Data
  // endpoint. Nuking CacheStorage + the service worker from JS is not
  // enough — the browser's HTTP disk cache still holds the old
  // main.HASH.js chunks, so `location.reload()` just re-serves them
  // and the same TDZ error fires again (users end up in a loop on
  // this screen tapping Reload). The backend response wipes the HTTP
  // cache + storage BEFORE the browser starts loading the next
  // page's JS, so the fresh chunks are guaranteed to come from the
  // origin. Cookies are preserved so the user stays signed in.
  try {
    const currentPath = window.location.pathname + window.location.search;
    const to = encodeURIComponent(currentPath || '/');
    const api = process.env.REACT_APP_BACKEND_URL || '';
    // Prefer the API-prefixed URL so it hits the backend origin
    // through the ingress. Fall back to same-origin if the env var
    // isn't set for some reason.
    const url = api
      ? `${api}/api/heal?to=${to}&_r=${Date.now()}`
      : `/api/heal?to=${to}&_r=${Date.now()}`;
    window.location.replace(url);
    return;
  } catch { /* fall through */ }
  // Last-resort fallback if the heal URL construction fails somehow.
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('_r', String(Date.now()));
    window.location.replace(url.toString());
  } catch {
    try { window.location.reload(); } catch { /* ignore */ }
  }
}

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      message: '',
      stack: '',
      recovering: false,
    };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      message: error?.message || 'Something went wrong.',
      stack: (error?.stack || '').slice(0, 600),
    };
  }

  componentDidMount() {
    // Bridge async errors (unhandled promise rejections + top-level
    // window errors) into the boundary so they trip the same self-heal
    // path instead of hanging the app in a broken state.
    this._onError = (e) => {
      if (isStaleChunkError(e?.error || e?.reason || e?.message)) {
        this.attemptSelfHeal(e?.error || e?.reason || new Error(String(e?.message || '')));
      }
    };
    this._onRejection = (e) => {
      if (isStaleChunkError(e?.reason)) {
        this.attemptSelfHeal(e?.reason);
      }
    };
    window.addEventListener('error', this._onError);
    window.addEventListener('unhandledrejection', this._onRejection);
  }

  componentWillUnmount() {
    window.removeEventListener('error', this._onError);
    window.removeEventListener('unhandledrejection', this._onRejection);
  }

  componentDidCatch(error, info) {
    console.error('[ErrorBoundary]', error, info?.componentStack);
    // Best-effort backend log — swallowed if network is down.
    try {
      const backend = process.env.REACT_APP_BACKEND_URL || '';
      const url = `${backend}/api/track/error`;
      const body = JSON.stringify({
        message: error?.message,
        stack: error?.stack?.slice(0, 4000),
        componentStack: info?.componentStack?.slice(0, 4000),
        url: window.location.href,
        ua: navigator.userAgent,
        healed_at: sessionStorage.getItem('dl_healed_at') || null,
      });
      try { localStorage.setItem('dl_last_crash_v1', body); } catch { /* ignore */ }
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
        credentials: 'omit',
      }).catch(() => { /* fallback already stored */ });
      // iter307 · Also mirror the event to the Health Watchdog
      // endpoint so the admin dashboard sees it + can trigger the
      // hourly agent's user-outreach flow. Uses `credentials:
      // include` so signed-in users are attributed by cookie.
      fetch(`${backend}/api/health/report-error`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: (error?.message || '').slice(0, 1000),
          stack:   (error?.stack   || '').slice(0, 4000),
          path:    window.location.pathname,
          component: (info?.componentStack || '').split('\n')[1]?.trim().slice(0, 200) || null,
          kind:    'render',
          user_agent: navigator.userAgent,
        }),
        keepalive: true,
        credentials: 'include',
      }).catch(() => { /* health mirror is best-effort */ });
    } catch { /* ignore */ }
    // Auto-recovery — if this is the "stale chunk" family AND we
    // haven't already tried a heal this session, kick it off now so
    // the user never sees the fallback UI. If we already tried,
    // we fall through to the friendly screen.
    if (isStaleChunkError(error)) {
      this.attemptSelfHeal(error);
    }
  }

  attemptSelfHeal = async (_error) => {
    let recent = null;
    try {
      recent = sessionStorage.getItem(RECOVERY_FLAG);
    } catch { /* ignore */ }
    // If we've healed within the last minute already, don't loop —
    // just show the friendly screen so the user can decide.
    if (recent && Date.now() - Number(recent) < RECOVERY_TTL_MS) {
      return;
    }
    try { sessionStorage.setItem(RECOVERY_FLAG, String(Date.now())); } catch { /* ignore */ }
    this.setState({ recovering: true });
    await nukeAllCaches();
    // Small delay so the "Getting the freshest version…" copy is
    // actually readable — otherwise the reload flash is disorienting.
    setTimeout(hardReload, 700);
  };

  handleReload = async () => {
    this.setState({ recovering: true });
    await nukeAllCaches();
    setTimeout(hardReload, 500);
  };

  handleHome = () => {
    try {
      // Also clear caches when the user chooses "Take me home" so we
      // don't drop them into another stale-chunk trap on the next
      // route. Fire-and-forget; navigation happens regardless.
      nukeAllCaches().catch(() => {});
      // iter364 · On native iOS, window.location.assign('/') was
      // taking users out of the Capacitor bundle context. Use a
      // hash change (SPA-friendly) so we stay in-app.
      const isNative = typeof window !== 'undefined' && window?.Capacitor?.isNativePlatform?.();
      if (isNative) {
        try {
          window.history.pushState({}, '', '/');
          window.dispatchEvent(new PopStateEvent('popstate'));
          this.setState({ hasError: false, message: '', stack: '' });
          return;
        } catch { /* fall through */ }
      }
      window.location.assign('/');
    } catch { /* ignore */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    // While a self-heal is in flight, show a calming "we've got this"
    // spinner instead of an error card — the reload usually completes
    // in < 1s so the user's just briefly sees this state.
    if (this.state.recovering) {
      return (
        <div
          className="min-h-screen flex items-center justify-center bg-black text-white px-6"
          style={{ backgroundColor: '#000' }}
          data-testid="error-boundary-recovering"
        >
          <div className="max-w-md text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-white/[0.06] border border-white/15 flex items-center justify-center mb-5">
              <svg className="w-6 h-6 animate-spin text-white/80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity=".25" strokeWidth="3" />
                <path d="M22 12A10 10 0 0 0 12 2" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
              </svg>
            </div>
            <h1 className="font-display text-2xl uppercase mb-2 leading-tight">Grabbing the freshest version</h1>
            <p className="text-sm text-white/60 leading-relaxed">
              Flythouse just updated in the background. One second while we swap in the new files…
            </p>
          </div>
        </div>
      );
    }

    // Friendly recovery screen — warm, brand-forward, no scary red X,
    // no stack trace open by default.
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-black text-white px-6"
        style={{ backgroundColor: '#000' }}
        data-testid="error-boundary"
      >
        <div className="max-w-md w-full text-center">
          {/* Warm sunset gradient badge instead of a red X */}
          <div
            className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-6"
            style={{
              background: 'linear-gradient(135deg, #F5C043 0%, #F97316 60%, #EF4444 100%)',
              boxShadow: '0 8px 24px -8px rgba(245,192,67,0.4)',
            }}
            aria-hidden="true"
          >
            <svg className="w-8 h-8 text-black" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2v6" />
              <path d="M12 22v-2" />
              <path d="m4.93 4.93 4.24 4.24" />
              <path d="m14.83 9.17 4.24-4.24" />
              <path d="M2 12h6" />
              <path d="M22 12h-2" />
              <path d="m4.93 19.07 4.24-4.24" />
              <path d="m14.83 14.83 4.24 4.24" />
            </svg>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl uppercase mb-3 leading-tight" style={{ letterSpacing: '-0.01em' }}>
            Give us a sec
          </h1>
          <p className="text-sm text-white/70 mb-2 leading-relaxed">
            We just rolled out a fresh update and your app needs to pick up the new files.
            Nothing&apos;s broken and nothing&apos;s lost — one tap fixes it.
          </p>
          <p className="text-xs text-white/40 mb-8">
            All your stories, links, and follows are safe on your account.
          </p>
          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={this.handleReload}
              className="w-full px-5 py-3.5 rounded-full text-black text-sm font-bold active:scale-95 transition"
              style={{ background: 'linear-gradient(135deg, #F5C043 0%, #F97316 100%)', boxShadow: '0 4px 16px -4px rgba(249,115,22,0.4)' }}
              data-testid="error-reload-btn"
            >
              Reload with fresh files
            </button>
            <button
              type="button"
              onClick={this.handleHome}
              className="w-full px-5 py-3 rounded-full border border-white/20 text-white text-sm font-bold hover:bg-white/[0.06] active:scale-95 transition"
              data-testid="error-home-btn"
            >
              Take me home
            </button>
          </div>
          {this.state.message && (
            <details className="mt-8 text-left text-[11px] text-white/50" data-testid="error-technical-details">
              <summary className="cursor-pointer text-white/50 hover:text-white/70 transition">Technical details (for the nerds)</summary>
              <p className="mt-2 font-mono text-white/70 break-words">{this.state.message}</p>
              {this.state.stack && (
                <pre className="mt-2 whitespace-pre-wrap break-words text-white/30 text-[10px] max-h-40 overflow-auto">{this.state.stack}</pre>
              )}
              <p className="mt-2 text-white/30 text-[10px]">URL: {typeof window !== 'undefined' ? window.location.pathname : ''}</p>
            </details>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
